import { useQuery } from "@tanstack/react-query";
import { auth } from "../../../components/Auth/Auth.jsx";

/**
 * Fetches the monthly report for the selected shop.
 *
 * @param {Object} params
 * @param {string} params.shopId - Selected shop ID.
 * @param {number} params.year - Selected report year.
 * @param {number} params.month - Selected report month.
 * @returns {Promise<Object>} Monthly report returned by the backend.
 */
const GetMonthlyReport = async ({ shopId, year, month }) => {
  // Ensure the user is authenticated before calling the API.
  const user = auth.currentUser;

  if (!user) {
    throw new Error("No authenticated user found");
  }

  // Retrieve the Firebase authentication token.
  const token = await user.getIdToken();

  // Encode the formatted shop ID so special characters are safe inside the URL.
  const encodedShopId = encodeURIComponent(String(shopId));

  // Request the monthly report from the backend.
  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/api/v1/monthly-report/shops/${encodedShopId}?year=${year}&month=${month}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  // Handle request errors.
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Unauthorized: Please log in again.");
    }

    throw new Error("Unable to load monthly report.");
  }

  // Return the parsed JSON response.
  return response.json();
};

/**
 * React Query hook used to load the monthly report.
 *
 * The query is disabled by default and should be triggered
 * manually using the returned refetch() function.
 */
export const useMonthlyReportData = (shopId, year, month, options = {}) => {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: ["MonthlyReport", shopId, year, month],
    queryFn: () =>
      GetMonthlyReport({
        shopId,
        year,
        month,
      }),
    enabled: false,
    ...options,
  });

  return {
    data,
    isPending,
    isFetching,
    error,
    refetch,
  };
};
